import {useEffect, useMemo} from 'react';
import {useAppSelector, useModal} from '@hooks';
import {
  getSelectedBuildingNodeId,
  getFloorsByBuildingIdSorted,
  getInitialSelectedWorkspace,
  getAvailableDeskAmenityDataByBuildingId,
  getAvailableActivityTypeDataByBuildingId,
  withAsyncThunkErrorHandling,
  getAvailableDeskAmenitiesForBuilding,
  getAvailableActivityTypesForBuilding,
  useAppDispatch,
  useOverridableFeatureFlags,
} from '@lib/store';
import {ChipFilterNew} from '@molecules';
import {WorkspaceSelectionCell, ModalPage} from '@organisms';
import {Div, FlexCol, FlexRow, P, Subtitle} from '@quarks';
import {useTranslation} from 'react-i18next';
import {getDeskAmenityIcon} from '@utils';
import {Illustration} from '@atoms';
import {constructWorkspacesFilter, useFilterData} from '@lib/logic';
import {getListOfSelectableWorkingSpacesByTypeNew} from '@lib/store/src/data/timeslots/selectors';
import {LocalTimespan, OfficeWorkday} from '@lib/store/src/data/timeslots/apiTypes';
import {WorkspaceAvailabilityWarning} from 'src/components/molecules/WorkspaceAvailabilityWarning';

export type WorkspaceSelectionCardProps = {
  date: string;
  workday?: OfficeWorkday;
  inviteeCount?: number;
  timespan: LocalTimespan;
};

export const WorkspaceSelectionCard = ({date, workday, timespan, inviteeCount = 0}: WorkspaceSelectionCardProps) => {
  const features = useOverridableFeatureFlags();
  const {closeModal, removePages} = useModal();
  const {t} = useTranslation();
  const dispatch = useAppDispatch();

  const initialSelectionParams = useMemo(() => ({selectedDate: date}), [date]);
  const baseLocation = useAppSelector((state) => getInitialSelectedWorkspace(state, initialSelectionParams));
  const selectedBuildingId = (useAppSelector(getSelectedBuildingNodeId) || baseLocation?.buildingId) ?? '';
  const floors = useAppSelector(getFloorsByBuildingIdSorted(selectedBuildingId));

  const availableDeskAmenityData = useAppSelector(getAvailableDeskAmenityDataByBuildingId(selectedBuildingId));
  const availableActivityTypeData = useAppSelector(getAvailableActivityTypeDataByBuildingId(selectedBuildingId));
  const {availableDeskAmenities} = availableDeskAmenityData;
  const {availableActivityTypes} = availableActivityTypeData;

  useEffect(() => {
    if (!selectedBuildingId) return;

    if (availableDeskAmenityData.status === 'NotLoaded') {
      dispatch(
        withAsyncThunkErrorHandling(() => getAvailableDeskAmenitiesForBuilding({buildingId: selectedBuildingId})),
      );
    }

    if (availableActivityTypeData.status === 'NotLoaded') {
      dispatch(
        withAsyncThunkErrorHandling(() => getAvailableActivityTypesForBuilding({buildingId: selectedBuildingId})),
      );
    }
  }, [dispatch, selectedBuildingId, availableDeskAmenityData.status, availableActivityTypeData.status]);

  const listOfSelectableWorkingSpaces = useAppSelector((state) => {
    if (!workday) {
      console.warn('Warning, workday is required if ff is enabled');
      return {
        Available: [],
        Unavailable: [],
      };
    }

    return getListOfSelectableWorkingSpacesByTypeNew(state, {
      workday: {
        ...workday,
        localStart: timespan.localStart,
        localEnd: timespan.localEnd,
      },
      inviteeCount,
    });
  });

  const filters = useMemo(
    () => constructWorkspacesFilter(floors, availableDeskAmenities, availableActivityTypes, getDeskAmenityIcon, t),
    [availableActivityTypes, availableDeskAmenities, floors, t],
  );
  const {filteredData: filteredAvailable} = useFilterData({
    sliceKey: 'CalendarOverview_Workspaces',
    cacheKeyInput: selectedBuildingId,
    optionsToFilter: listOfSelectableWorkingSpaces.Available,
    filtersInput: filters,
  });
  const {filteredData: filteredUnavailable} = useFilterData({
    sliceKey: 'CalendarOverview_Workspaces',
    cacheKeyInput: selectedBuildingId,
    optionsToFilter: listOfSelectableWorkingSpaces.Unavailable,
    filtersInput: filters,
  });

  const hasNoResults = !filteredAvailable.length && !filteredUnavailable.length;
  const allData = [
    {key: 'Available', data: filteredAvailable},
    {
      key: 'Unavailable',
      data: filteredUnavailable.filter((ws) => ws.workspaceBookingPolicyType !== 'NonBookable'),
    },
    {
      key: 'AdHoc',
      data: filteredUnavailable.filter((ws) => ws.workspaceBookingPolicyType === 'NonBookable'),
    },
  ];

  return (
    <ModalPage
      title={t('workplace:SelectWorkspace')}
      onBack={() => removePages(1)}
      onClose={closeModal}>
      <FlexCol gap={32}>
        <FlexRow
          justifyContent="flex-start"
          gap={8}>
          <ChipFilterNew
            filterSlice="CalendarOverview_Workspaces"
            areFilterTransparent
            goBackFromFilterModal={() => removePages(1)}
          />
        </FlexRow>

        {features.TimeslotsUI && workday && (
          <WorkspaceAvailabilityWarning
            workday={workday}
            workspace={
              baseLocation ?? {
                buildingId: selectedBuildingId,
              }
            }
            localStart={timespan.localStart}
            localEnd={timespan.localEnd}
          />
        )}

        {hasNoResults ? (
          <FlexCol
            alignItems="center"
            padding={32}
            gap={16}>
            <Illustration
              size="100px"
              illustration="noResults"
            />
            <P
              color="currentColor"
              textAlign="center">
              {t('workplace:CouldNotFindWorkspace')}
            </P>
          </FlexCol>
        ) : (
          <FlexCol gap={32}>
            {allData
              .filter(({data}) => {
                if (data.length) return true;
                return false;
              })
              .map((section) => (
                <FlexCol
                  gap={8}
                  key={section.key}>
                  {section.key === 'Unavailable' && (
                    <P
                      fontWeight="bold"
                      color="currentColor">
                      {t('workplace:WorkspaceGroupUnavailable')}
                    </P>
                  )}
                  {section.key === 'AdHoc' && (
                    <P
                      fontWeight="bold"
                      color="currenColor">
                      {t('AdHocWorkspaces')}
                    </P>
                  )}
                  <Div>
                    {section.data.map((workspace, index) => (
                      <WorkspaceSelectionCell
                        date={date}
                        workday={workday}
                        workspace={workspace}
                        inviteeCount={inviteeCount}
                        key={`workspace-${index}`}
                        timespan={timespan}
                      />
                    ))}
                  </Div>
                </FlexCol>
              ))}
            <Subtitle fontSize="sm">{t('workplace:NoWorkplaceInTheList')}</Subtitle>
          </FlexCol>
        )}
      </FlexCol>
    </ModalPage>
  );
};
